import React, { useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { PrimaryColors } from 'src/types/globalTypes';
import styled from 'styled-components/macro';
import * as colors from 'src/styles/colors';
import { useStore } from 'src/hooks/useStore';
import { FaRegFilePdf } from 'react-icons/fa';
import { mobilePoint } from 'src/styles/globalStyle';

const Wrapper = styled.div<{ primaryColors?: PrimaryColors }>`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${(props) =>
    props.primaryColors ? props.primaryColors.first : colors.primaryBlue_30};
  border: 2px dashed
    ${(props) => (props.primaryColors ? props.primaryColors.forth : colors.primaryBlue_200)};
  border-radius: 4px;
  width: 100%;
  padding: 2em 0;
  color: ${colors.BlueGray_200};
  cursor: default;
`;
const Button = styled.button<{ primaryColors?: PrimaryColors }>`
  background-image: linear-gradient(
    to top left,
    ${(props) => (props.primaryColors ? props.primaryColors.fifth : colors.primaryBlue_300)},
    ${(props) => (props.primaryColors ? props.primaryColors.forth : colors.primaryBlue_200)}
  );
  border-radius: 20px;
  padding: 6px 16px;
  font-family: NotoSansTC-Medium;
  color: ${colors.white};
  cursor: pointer;
`;
const Content = styled.div<{ primaryColors?: PrimaryColors }>`
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${mobilePoint}) {
    display: grid;
    justify-items: center;
    color: ${(props) => (props.primaryColors ? props.primaryColors.fifth : colors.primaryBlue_300)};
  }
`;
const Icon = styled.div`
  font-size: 2em;
`;
const FileDiv = styled.div`
  display: block;
  color: ${colors.BlueGray_800};
`;

interface IProps {
  setPdfFile: (pdfFiles: File) => void;
  setIsFileReject?: (isReject: boolean) => void;
  primaryColors?: PrimaryColors;
}

/**
 * PDF拖曳上傳
 */
const PdfUpload: React.FC<IProps> = ({ setPdfFile, setIsFileReject, primaryColors }) => {
  const { acceptedFiles, fileRejections, getRootProps, getInputProps } = useDropzone({
    accept: {
      'application/pdf': ['.pdf'],
    },
    maxSize: 10485760,
    multiple: false,
  });
  const isMobile = useStore((state) => state.isMobile);

  // 上傳檔案是否被拒
  useEffect(() => {
    setIsFileReject && setIsFileReject(fileRejections.length !== 0);
  }, [setIsFileReject, fileRejections]);

  // 插入上傳檔案
  useEffect(() => {
    setPdfFile(acceptedFiles[0]);
  }, [setPdfFile, acceptedFiles]);

  /** 已上傳的檔案 */
  const files = (
    <FileDiv>
      {acceptedFiles.map((file) => (
        <li key={file.name}>{file.name}</li>
      ))}
    </FileDiv>
  );

  const desktopForm = (
    <Content>
      {acceptedFiles.length === 0 ? (
        <>
          <span>將檔案放到這裡&nbsp;或&nbsp;</span>
          <Button primaryColors={primaryColors}>選擇檔案</Button>
        </>
      ) : (
        <>
          {files}&nbsp;&nbsp;
          <Button primaryColors={primaryColors}>重新選擇檔案</Button>
        </>
      )}
    </Content>
  );

  const mobileForm = (
    <Content primaryColors={primaryColors}>
      {acceptedFiles.length === 0 ? (
        <>
          <Icon>
            <FaRegFilePdf />
          </Icon>
          <div>選擇檔案</div>
        </>
      ) : (
        <>
          {files}&nbsp;&nbsp;
          <Button primaryColors={primaryColors}>重新選擇檔案</Button>
        </>
      )}
    </Content>
  );

  return (
    <Wrapper primaryColors={primaryColors}>
      <div {...getRootProps({ className: 'dropzone' })}>
        <input title="將電子證書PDF檔案放到這裡上傳" {...getInputProps()} />
        {isMobile ? mobileForm : desktopForm}
      </div>
    </Wrapper>
  );
};

export default PdfUpload;
